/**
 * Pillar page configs — iter 172 SEO reposition.
 *
 * Each config powers one indexable pillar landing page targeting a
 * high-intent commercial keyword cluster. The `PillarPage` component
 * consumes these directly.
 *
 * When you add a new pillar here:
 *   1. Register the route in App.js
 *   2. Add the slug to SITEMAP_STATIC_PATHS in backend/server.py
 *   3. Add a corresponding backend sitemap-lint entry if applicable
 *
 * Editorial rules for copy in this file:
 *   - Everything must be factually accurate. No "1M+ hospitals" claims.
 *   - No fabricated capacity / availability / provider quality data.
 *   - Every claim should be independently defensible.
 *   - Voice: professional, direct, benefits-first, no marketing fluff.
 */
import {
  ShieldCheck, Bot, ClipboardCheck, LineChart, Users, Inbox,
  Gauge, PenLine, Building2, Hospital, HandMetal, Timer,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// /discharge-planning-software  — head term for hospital DP SW
// ─────────────────────────────────────────────────────────────
export const dischargePlanningSoftwareConfig = {
  slug: 'discharge-planning-software',
  title: 'Discharge Planning Software',
  metaTitle: 'Discharge Planning Software for Hospitals & Case Managers | CaraLoom',
  metaDescription:
    'Discharge planning software that finds the right post-acute provider, coordinates referrals, and accelerates safe hospital-to-home transitions. Purpose-built for case managers and health systems.',
  keywords: [
    'discharge planning software',
    'hospital discharge planning software',
    'discharge planner software',
    'case management software',
    'post acute referral software',
    'care transition software',
  ],
  eyebrow: 'Discharge Planning Platform',
  h1: 'Discharge planning software that clears the exception queue by lunchtime.',
  subhead:
    'Case managers open CaraLoom instead of ten browser tabs, a fax cover sheet, and a spreadsheet. Referrals, coverage checks, patient choice, and signed orders live in one auditable loop.',
  heroSupport:
    'Built with discharge planners, not just for them. Every action is logged. Every provider offer is scored. Every hand-off is trackable.',
  primaryCta: { label: 'Request a hospital demo', href: '/contact?type=demo' },
  secondaryCta: { label: 'How the planner uses it', href: '/for-discharge-planners' },

  benefitsHeading: 'What discharge planning software should actually do',
  benefitsSubhead: 'CaraLoom is designed around the case-manager workday — not a legacy referral fax queue.',
  benefits: [
    { icon: Inbox, k: 'Exception queue', title: 'One inbox for everything stuck',
      body: 'Coverage holds, declined offers, expired responses, missing signatures — one prioritized queue with the exact next action on each card.' },
    { icon: ShieldCheck, k: 'Coverage first', title: 'Eligibility runs inside the loop',
      body: 'Payer eligibility, authorization, and network status resolve before an order routes for signature. Problems pause the loop and ask a human — never fail silently.' },
    { icon: Bot, k: 'Automated matching', title: 'The system does the phone tree',
      body: 'Scoring, ranking, and offer waves run themselves against your verified post-acute network. You see who was offered, when, and why — a full trace on every case.' },
    { icon: PenLine, k: 'Orders in one thread', title: 'F2F, 485, NOA, consent — signed in-platform',
      body: 'The standard order set drafts once coverage clears and is signed digitally. No fax loops, no lost forms, no reprinting for a corrected signature.' },
    { icon: LineChart, k: 'Length-of-stay lens', title: 'Avoidable-day analytics your CFO can read',
      body: 'Every hand-off is timestamped. Report on average time-to-placement, decline reasons, and avoidable days by unit, service line, and payer.' },
    { icon: Users, k: 'Patient choice, respected', title: 'Meets CMS choice requirements out of the box',
      body: 'Patients and families see the same ranked provider list the planner sees. Choice is documented, dated, and stored in the record — audit-ready.' },
  ],

  workflowHeading: 'The CaraLoom discharge loop',
  workflowSubhead: 'Search → compare → refer → place → track — every step accountable.',
  workflow: [
    { n: '1', title: 'Search',   body: 'Search your verified post-acute network by level of care, payer, geography, and clinical capability. Filters that match how case managers actually think.' },
    { n: '2', title: 'Compare', body: 'Side-by-side provider comparison shows verified capabilities, service area, insurance accepted, and current response signal. Never fabricated availability.' },
    { n: '3', title: 'Refer',    body: 'Send a HIPAA-safe referral packet to your shortlisted providers in one action. Offer waves are automatic; escalation rules are yours to set.' },
    { n: '4', title: 'Place',    body: 'First credentialed provider to accept wins. Coverage was already checked. The order set signs in-platform. Patient choice is captured on record.' },
    { n: '5', title: 'Track',    body: 'Every hand-off, every day of delay, and every barrier is timestamped. Length-of-stay analytics become a report your leadership can actually act on.' },
  ],

  audienceHeading: 'Different seats, one system',
  audiencePaths: [
    { title: 'Hospital case managers', body: 'Trade tabs, faxes, and spreadsheets for one exception queue.', cta: 'See the planner workflow', href: '/for-discharge-planners' },
    { title: 'Health-system leaders',  body: 'Reduce avoidable days without adding case-management FTEs.',    cta: 'Request a pilot',           href: '/contact?type=demo' },
    { title: 'Post-acute providers',   body: 'Get referrals with verified coverage, ready-to-sign orders.',   cta: 'Talk to us',                href: '/contact?type=provider' },
  ],

  faqHeading: 'Discharge planning software — the honest answers',
  faq: [
    { q: 'What is discharge planning software?',
      a: 'Discharge planning software is a workflow platform used by hospital case managers and social workers to identify a patient\u2019s post-acute needs, find qualified providers (SNF, home health, hospice, IRF, LTACH), obtain patient choice, coordinate referrals, verify coverage, and track the transition until placement is complete.' },
    { q: 'How is CaraLoom different from CarePort or Aidin?',
      a: 'CaraLoom orients around the case-manager\u2019s exception queue and treats coverage as a first-class step in the loop instead of a hand-off. Every action is auditable, patient-choice documentation is native, and the platform is API-first so hospitals can integrate it with their EHR, HIE, and payer workflows.' },
    { q: 'Does CaraLoom integrate with our EHR?',
      a: 'Yes. CaraLoom exposes an API-first surface and supports FHIR, HL7, and secure eFax ingestion so referral packets flow in from any Epic, Cerner/Oracle Health, MEDITECH, or Athenahealth environment.' },
    { q: 'Is CaraLoom HIPAA-compliant?',
      a: 'Yes. CaraLoom is architected for HIPAA — encryption in transit and at rest, tenant-scoped data isolation, MFA for clinicians and admins, BAA-gated LLM routing, and audit logging on every PHI-touching action.' },
    { q: 'Can our patients see the same provider list we see?',
      a: 'Yes. CMS requires patient choice; CaraLoom documents it. Patients and families see the ranked list of qualifying providers, choose from it, and their choice is stamped into the record.' },
    { q: 'How do you avoid recommending providers that can\u2019t take the patient?',
      a: 'Provider capability, payer acceptance, and service-area data are verified up front. Live response signals reflect actual acceptance history — CaraLoom never fabricates availability or manufactures acceptance rates.' },
  ],

  softwareFeatures: [
    'Exception queue with prioritized next actions',
    'Automated post-acute provider matching',
    'CMS-compliant patient choice documentation',
    'Live eligibility and authorization checks',
    'Digital signature on F2F, 485, NOA, and consent',
    'Length-of-stay and avoidable-day analytics',
    'HIPAA-grade audit trail on every action',
    'HIE record retrieval via Metriport',
  ],

  bottomCtaTitle: 'See the discharge loop end-to-end',
  bottomCtaBody:
    'A 30-minute demo, using de-identified data, walking through search, patient choice, coverage, referral, placement, and analytics.',
};

// ─────────────────────────────────────────────────────────────
// /post-acute-care-platform  — category-owning positioning
// ─────────────────────────────────────────────────────────────
export const postAcuteCarePlatformConfig = {
  slug: 'post-acute-care-platform',
  title: 'Post-Acute Care Platform',
  metaTitle: 'Post-Acute Care Platform — Referral Management & Placement | CaraLoom',
  metaDescription:
    'The post-acute care platform for hospitals and health systems. Match patients to SNF, home health, hospice, IRF, and LTACH providers. Coordinate referrals, verify coverage, and track outcomes in one place.',
  keywords: [
    'post acute care platform',
    'post acute care software',
    'post acute referral platform',
    'post acute placement software',
    'post acute network management',
    'post acute provider search',
  ],
  eyebrow: 'Post-Acute Intelligence',
  h1: 'The post-acute care platform for the entire care transition.',
  subhead:
    'Every level of post-acute care — SNF, home health, hospice, IRF, LTACH — in one search, comparable side by side, referrable in one click, and trackable end to end.',
  heroSupport:
    'CaraLoom becomes the operating system between the hospital bed and the next site of care. Nothing on this platform is fabricated: providers verify their own capabilities, and coverage is checked against real payer rules.',

  benefitsHeading: 'One platform, every level of care',
  benefits: [
    { icon: Building2, k: 'Provider graph',      title: 'Every level, every geography',
      body: 'Skilled nursing, home health, hospice, IRF, LTACH, assisted living, memory care, DME — searchable by service area, payer, and clinical capability.' },
    { icon: Gauge,     k: 'Access-time metrics', title: 'Time-to-placement, decline reasons, geographic gaps',
      body: 'A dashboard of the metrics that actually move length-of-stay: median time-to-placement by level of care, decline categories, and payer trends.' },
    { icon: Hospital,  k: 'Health-system view',  title: 'Roll up across hospitals in your system',
      body: 'Compare access, placement, and network utilization across every hospital in your system. Data your COO and network-strategy leaders can act on.' },
    { icon: HandMetal, k: 'Network management',  title: 'Manage your preferred network without a spreadsheet',
      body: 'Preferred, in-network, and out-of-network tiers. Utilization, outcomes, and response signal by provider. Data-driven contracting instead of anecdotal.' },
    { icon: Timer,     k: 'Faster referrals',    title: 'Cut a discharge day out of the loop',
      body: 'Automated offer waves and pre-checked coverage remove the human-touch delays that stack up between admission and post-acute placement.' },
    { icon: ClipboardCheck, k: 'Auditable',      title: 'HIPAA-grade audit on every touch',
      body: 'Every referral, response, override, and choice is logged with actor + timestamp. Compliance ready by default, not as a bolt-on.' },
  ],

  workflowHeading: 'The five loops of a post-acute platform',
  workflow: [
    { n: '1', title: 'Discovery',      body: 'Search across the entire post-acute ecosystem. Not just one modality — every relevant level of care for this patient in one query.' },
    { n: '2', title: 'Compare & rank', body: 'Verified capability, distance, payer acceptance, and current response signal — the four decision factors, ranked.' },
    { n: '3', title: 'Refer & route',  body: 'Structured referral packets with the right documents, sent to the right provider, at the right level of care.' },
    { n: '4', title: 'Placement',      body: 'First accept wins. Coverage is already checked. Orders sign digitally. Nothing final until a real clinician confirms.' },
    { n: '5', title: 'Analyze',        body: 'Feed every hand-off back into your network strategy. See who\u2019s underperforming. See where access is failing.' },
  ],

  audienceHeading: 'Every stakeholder in the post-acute value chain',
  audiencePaths: [
    { title: 'Hospitals & health systems', body: 'One platform across every hospital, every discharge planner, every payer.', cta: 'Explore health-system fit', href: '/contact?type=demo' },
    { title: 'Case managers',              body: 'Move faster through the exception queue and document choice.',              cta: 'See the workflow',        href: '/for-discharge-planners' },
    { title: 'Post-acute providers',       body: 'Show up in the right searches with verified capabilities.',                 cta: 'Talk to us',              href: '/contact?type=provider' },
  ],

  faqHeading: 'What a post-acute care platform should do',
  faq: [
    { q: 'What is a post-acute care platform?',
      a: 'A post-acute care platform is the software layer between a hospital and the next site of care (SNF, home health, hospice, IRF, LTACH, assisted living). It unifies provider discovery, referral management, patient-choice capture, coverage checking, and outcome tracking so care transitions happen faster and safer.' },
    { q: 'How is CaraLoom different from a legacy referral tool?',
      a: 'Legacy referral tools were built around fax queues; CaraLoom is built around the case-manager\u2019s exception queue with coverage and patient choice inside the loop, not adjacent to it. It\u2019s API-first, HIPAA-grade, and every provider record is verified — not fabricated.' },
    { q: 'Which post-acute levels of care do you cover?',
      a: 'Skilled nursing facilities (SNF), home health agencies, hospice, inpatient rehabilitation facilities (IRF), LTACHs, assisted living, memory care, palliative care, and durable medical equipment (DME).' },
    { q: 'Do you pull in external clinical history?',
      a: 'Yes. CaraLoom can query health information exchanges (CommonWell and Carequality via Metriport) to enrich a patient\u2019s intake packet with prior H&Ps, diagnoses, medications, labs, encounters, and previous hospitalizations — with documented patient consent.' },
  ],

  softwareFeatures: [
    'Search across SNF, home health, hospice, IRF, LTACH, and more',
    'Verified provider capability and service-area data',
    'HIE record retrieval (CommonWell + Carequality via Metriport)',
    'Network utilization and access-time analytics',
    'Preferred / in-network / out-of-network tiering',
    'CMS-compliant patient choice documentation',
    'FHIR + HL7 integration surface',
  ],

  bottomCtaTitle: 'Own your post-acute layer',
  bottomCtaBody:
    'CaraLoom lets hospitals stop losing patients into black boxes after discharge. See the whole post-acute layer in one demo.',
};

// ─────────────────────────────────────────────────────────────
// /care-transitions — care-transition software category
// ─────────────────────────────────────────────────────────────
export const careTransitionsConfig = {
  slug: 'care-transitions',
  title: 'Care Transitions',
  metaTitle: 'Care Transition Software — Coordinate Every Hand-Off | CaraLoom',
  metaDescription:
    'Care-transition software that removes the seams between hospital, post-acute, and home. Referrals, coverage, patient choice, and tracked outcomes across every hand-off — designed for the case-management team.',
  keywords: [
    'care transition software',
    'care coordination software',
    'care transitions platform',
    'patient placement software',
    'hospital case management software',
    'reduce hospital length of stay',
  ],
  eyebrow: 'Care Transition Coordination',
  h1: 'Care transitions that don\u2019t fall through the seams.',
  subhead:
    'Between the hospital bed and the next site of care, most information is lost, most decisions happen offline, and most delay is invisible. CaraLoom pulls the transition into one accountable loop.',
  heroSupport:
    'A single system for referrals, coverage, patient choice, orders, and outcome tracking — with every action logged, every day of delay counted, and every hand-off measurable.',

  benefitsHeading: 'What better care transitions look like',
  benefits: [
    { icon: Timer,      k: 'No dead time',        title: 'Nothing waits on a fax that never arrived',
      body: 'Referral packets, coverage checks, order sets, and signatures live in one thread. If someone hasn\u2019t responded, you see it — you don\u2019t discover it a day later.' },
    { icon: ShieldCheck,k: 'Coverage inside the loop', title: 'Eligibility, auth, and network status — before signature',
      body: 'You never send a patient to a provider you can\u2019t bill for. Coverage is verified in-loop. Problems block the loop and ask a human.' },
    { icon: Users,      k: 'Patient choice',      title: 'Documented, not implied',
      body: 'Patients see the same qualifying providers you see. Choice is captured on record and time-stamped — the CMS documentation checklist stops being a Friday-afternoon scramble.' },
    { icon: ClipboardCheck, k: 'Full audit trail', title: 'Every decision is defensible',
      body: 'Who saw it, when, and what they did — on every referral, every override, every decline. Compliance reviews become 30-minute reviews, not week-long archaeologies.' },
    { icon: LineChart,  k: 'Length-of-stay lens', title: 'Track avoidable days by service line',
      body: 'Which units, which payers, and which post-acute levels are dragging your average LOS? See it, own it, fix it.' },
    { icon: Bot,        k: 'Automation, not autopilot', title: 'Machines do the phone tree — humans keep the call',
      body: 'Offer waves, ranking, and escalation are automatic. Placement, override, and signature stay with the humans who own the outcome.' },
  ],

  workflowHeading: 'Every care transition, one loop',
  workflow: [
    { n: '1', title: 'Trigger',     body: 'Discharge disposition or referral request kicks off a transition workflow with the right level-of-care template.' },
    { n: '2', title: 'Match',       body: 'CaraLoom scores the qualifying provider list against payer, capability, geography, and current response signal.' },
    { n: '3', title: 'Choose',      body: 'The patient / family sees the ranked list. Choice is documented and time-stamped.' },
    { n: '4', title: 'Coordinate',  body: 'Offer waves fire automatically. Coverage is checked. Order set drafts. Signatures go digital.' },
    { n: '5', title: 'Track',       body: 'From referral through arrival, every hand-off is timestamped. Access dashboards show the whole system, live.' },
  ],

  audienceHeading: 'Every hand-off, every seat',
  audiencePaths: [
    { title: 'Case managers',        body: 'Move through your queue without leaving CaraLoom.', cta: 'See the workflow',    href: '/for-discharge-planners' },
    { title: 'Health-system leaders',body: 'Cut avoidable days across every hospital in the system.', cta: 'Request a demo',     href: '/contact?type=demo' },
    { title: 'Post-acute providers', body: 'Get referrals with clean documentation and verified coverage.', cta: 'Talk to us',      href: '/contact?type=provider' },
  ],

  faqHeading: 'Care transitions — the honest FAQ',
  faq: [
    { q: 'What is care-transition software?',
      a: 'Care-transition software coordinates the hand-off of a patient between sites of care — most commonly from a hospital to a post-acute setting like SNF, home health, hospice, IRF, or LTACH. Good care-transition software captures every hand-off, verifies coverage, documents patient choice, and tracks outcomes end-to-end.' },
    { q: 'How does CaraLoom reduce length of stay?',
      a: 'By removing the human-touch delays inside the referral loop — coverage checks happen in-line, offer waves fire automatically, order sets sign digitally, and the exception queue surfaces stalled cases immediately instead of a day later. Hospitals typically measure the impact in avoidable days per unit.' },
    { q: 'Do you replace our EHR?',
      a: 'No. CaraLoom integrates with your EHR (Epic, Cerner/Oracle Health, MEDITECH, Athenahealth) via FHIR and HL7 and lives alongside your existing clinical documentation. CaraLoom owns the referral/transition layer specifically.' },
    { q: 'How is patient choice handled?',
      a: 'CaraLoom generates the qualifying provider list, presents it to the patient and family, records the choice with a timestamp and user, and stores the documentation in the audit log — meeting CMS discharge-planning requirements natively.' },
  ],

  softwareFeatures: [
    'Referral, coverage, choice, orders, and outcome — in one loop',
    'Automated offer waves and escalation rules',
    'Documented CMS-compliant patient choice',
    'Length-of-stay + avoidable-day analytics',
    'Full audit trail with actor + timestamp on every action',
    'FHIR + HL7 EHR integration',
  ],

  bottomCtaTitle: 'Own the transition, not the fallout',
  bottomCtaBody:
    'A demo with your data model in mind — 30 minutes, no PHI required. Walk through a real hospital-to-post-acute transition end to end.',
};

export const ALL_PILLAR_CONFIGS = [
  dischargePlanningSoftwareConfig,
  postAcuteCarePlatformConfig,
  careTransitionsConfig,
];
